import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatCurrency, formatMoney } from '@/utils/format'

/** Single-series brand colour (validated against the light and dark surfaces). */
const BRAND = '#4f46e5'

interface TooltipPayload {
  value?: number | string
  color?: string
}

function MoneyTooltip({
  active,
  payload,
  label,
  currency,
}: {
  active?: boolean
  payload?: TooltipPayload[]
  label?: string | number
  currency: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-control border border-line bg-surface px-3 py-2 shadow-raised">
      {label !== undefined ? <p className="text-xs font-medium text-muted">{label}</p> : null}
      {payload.map((p, i) => (
        <p key={i} className="mt-0.5 flex items-center gap-2 text-sm font-semibold text-ink tabular">
          <span className="size-2 rounded-full" style={{ backgroundColor: p.color }} aria-hidden="true" />
          {formatMoney(Number(p.value), currency)}
        </p>
      ))}
    </div>
  )
}

function compact(value: number, currency: string): string {
  return formatCurrency(value, currency, { notation: 'compact', maximumFractionDigits: 1 })
}

interface SeriesPoint {
  key: string
  label: string
  value: number
}

/**
 * Vertical single-series bar chart for money over time, with a hover tooltip and a table view.
 */
export function MoneyBarChart({
  data,
  currency,
  ariaLabel,
  keyHeader,
}: {
  data: SeriesPoint[]
  currency: string
  ariaLabel: string
  keyHeader: string
}) {
  const { t } = useTranslation('finance')
  return (
    <figure>
      <div className="h-64" role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="3 4" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'var(--muted)' }}
              dy={6}
              interval="preserveStartEnd"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'var(--muted)' }}
              width={56}
              tickFormatter={(v: number) => compact(v, currency)}
            />
            <Tooltip
              content={<MoneyTooltip currency={currency} />}
              cursor={{ fill: 'var(--surface-sunken)' }}
            />
            <Bar dataKey="value" fill={BRAND} radius={[4, 4, 0, 0]} maxBarSize={36} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <details className="mt-2 text-xs text-muted">
        <summary className="cursor-pointer select-none hover:text-ink">{t('View as table')}</summary>
        <table className="mt-2 w-full text-left">
          <thead>
            <tr>
              <th className="py-1 font-medium">{keyHeader}</th>
              <th className="py-1 text-right font-medium">{t('Amount')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.key} className="border-t border-line">
                <td className="py-1">{d.label}</td>
                <td className="py-1 text-right tabular">{formatMoney(d.value, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  )
}

/** Horizontal ranked bars (e.g. outstanding by class) — one hue, labels in ink. */
export function RankedMoneyBars({
  rows,
  currency,
  ariaLabel,
}: {
  rows: { key: string | number; label: string; value: number; hint?: string }[]
  currency: string
  ariaLabel: string
}) {
  const max = Math.max(1, ...rows.map((r) => r.value))
  return (
    <ul className="space-y-3" aria-label={ariaLabel}>
      {rows.map((r) => (
        <li key={r.key}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-ink-soft">
              {r.label}
              {r.hint ? <span className="ml-1.5 text-xs text-muted">{r.hint}</span> : null}
            </span>
            <span className="font-medium text-ink tabular">{formatMoney(r.value, currency)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-sunken" aria-hidden="true">
            <div
              className="h-full rounded-full bg-brand-600"
              style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}
